import React from "react";
import PageHeader from "../Components/common/PageHeader";
import { useTranslation } from "react-i18next";
import { FaCheckCircle, FaClipboardList, FaMotorcycle, FaRoad } from "react-icons/fa";

function A2LicensePage() {
  const { i18n } = useTranslation();
  const isEnglish = i18n.language === "en";

  const content = isEnglish
    ? {
        pageTitle: "A2 License Guide",
        badge: "A2 License Guide",
        title: "Comprehensive Guide to the A2 Class Driving License",
        intro:
          "The A2 license covers mid-range motorcycles and offers a balanced training process for candidates who want to ride both in the city and on longer routes.",
        stats: [
          { label: "Minimum age", value: "18" },
          { label: "Maximum power", value: "35 kW" },
          { label: "Power ratio", value: "0.2 kW/kg" },
          { label: "Extra classes", value: "M + A1" },
        ],
        sideTitle: "A2 Scope",
        scope: [
          "Two-wheel motorcycles with a maximum power of 35 kW and power-to-weight ratio up to 0.2 kW/kg.",
          "Three-wheel motorcycles up to 15 kW.",
          "A2 license holders may also use M and A1 class vehicles.",
        ],
        requirementsTitle: "Application Requirements",
        requirements: [
          "Be at least 18 years old.",
          "Have completed at least primary school education.",
          "Obtain a valid driver medical report from an authorized institution.",
          "Not have convictions that create a legal obstacle for driver licensing.",
        ],
        processTitle: "Exam Process",
        process: [
          "First-time applicants attend theory training and must score at least 70 on the e-exam.",
          "Candidates who pass the theory stage continue with track and live-traffic riding lessons.",
          "Applicants who already hold classes such as B or A1 may be exempt from the theory exam.",
          "In that case, only the A2 practical training and riding test are completed.",
        ],
        documentsTitle: "Required Documents",
        documents: [
          "Photocopy of identity card",
          "4 biometric photos",
          "Education certificate or diploma copy",
          "Health report",
          "Criminal record declaration",
          "Copy of current license, if any",
        ],
      }
    : {
        pageTitle: "A2 Ehliyet Rehberi",
        badge: "A2 Ehliyet Rehberi",
        title: "A2 Sınıfı Sürücü Belgesi Kapsamlı Rehberi",
        intro:
          "A2 sınıfı ehliyet, orta sınıf motor gücüne sahip motosikletleri kapsayan ve hem şehir içi hem de daha uzun sürüşler için dengeli bir eğitim süreci sunan ehliyet sınıfıdır.",
        stats: [
          { label: "Yaş şartı", value: "18" },
          { label: "Azami güç", value: "35 kW" },
          { label: "Güç oranı", value: "0,2 kW/kg" },
          { label: "Ek yetki", value: "M + A1" },
        ],
        sideTitle: "A2 Kapsamı",
        scope: [
          "İki tekerlekli motosikletlerde güç sınırı 35 kW, güç/ağırlık oranı ise en fazla 0,2 kW/kg'dır.",
          "Üç tekerlekli motosikletlerde azami motor gücü 15 kW olabilir.",
          "A2 ehliyet sahipleri M ve A1 sınıfı araçları da ayrıca kullanabilir.",
        ],
        requirementsTitle: "Başvuru Şartları",
        requirements: [
          "En az 18 yaşını doldurmuş olmak.",
          "En az ilkokul mezunu olmak.",
          "Yetkili kurumlardan alınmış sürücü olur sağlık raporuna sahip olmak.",
          "Yasal engel oluşturan suçlardan hüküm giymemiş olmak.",
        ],
        processTitle: "Sınav Süreci",
        process: [
          "İlk kez ehliyet alacak adaylar teorik eğitime katılır ve e-Sınavdan en az 70 puan alır.",
          "Teorik aşamayı geçen adaylar parkur ve akan trafik eğitimine alınır.",
          "B, A1 gibi başka ehliyeti olan adaylar teorik sınavdan muaf tutulabilir.",
          "Bu durumda yalnızca A2 direksiyon eğitimi ve uygulama sınavı tamamlanır.",
        ],
        documentsTitle: "Gerekli Belgeler",
        documents: [
          "T.C. kimlik kartı fotokopisi",
          "4 adet biyometrik fotoğraf",
          "Öğrenim belgesi veya diploma örneği",
          "Sağlık raporu",
          "Adli sicil kaydı beyanı",
          "Varsa mevcut sürücü belgesi fotokopisi",
        ],
      };

  return (
    <>
      <PageHeader title={content.pageTitle} />
      <section className="license-guide-page">
        <div className="container">
          <div className="guide-hero-card">
            <div className="guide-hero-main">
              <span className="guide-badge">{content.badge}</span>
              <h2>{content.title}</h2>
              <p>{content.intro}</p>
              <div className="guide-stats">
                {content.stats.map((item) => (
                  <div key={item.label} className="guide-stat-card">
                    <span>{item.label}</span>
                    <strong>{item.value}</strong>
                  </div>
                ))}
              </div>
            </div>

            <div className="guide-hero-side">
              <div className="guide-side-icon"><FaMotorcycle /></div>
              <h3>{content.sideTitle}</h3>
              {content.scope.map((item) => (
                <div key={item} className="guide-side-item">{item}</div>
              ))}
            </div>
          </div>

          <div className="row g-4 mt-1">
            <div className="col-lg-6">
              <div className="guide-panel h-100">
                <div className="guide-panel-title"><FaCheckCircle /><h3>{content.requirementsTitle}</h3></div>
                {content.requirements.map((item) => (
                  <div key={item} className="guide-list-item"><FaCheckCircle /><span>{item}</span></div>
                ))}
              </div>
            </div>

            <div className="col-lg-6">
              <div className="guide-panel h-100">
                <div className="guide-panel-title"><FaRoad /><h3>{content.processTitle}</h3></div>
                {content.process.map((item, index) => (
                  <div key={item} className="guide-process-item">
                    <span className="guide-step">{index + 1}</span>
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="guide-section">
            <div className="guide-section-heading">
              <span>{content.documentsTitle}</span>
              <h3>{content.documentsTitle}</h3>
            </div>
            <div className="row g-4">
              {content.documents.map((item) => (
                <div className="col-lg-4 col-md-6" key={item}>
                  <div className="guide-doc-card h-100">
                    <div className="guide-doc-icon"><FaClipboardList /></div>
                    <p>{item}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      <style>{sharedStyles}</style>
    </>
  );
}

const sharedStyles = `
  .license-guide-page { padding: 40px 0 80px; background: linear-gradient(180deg,#fff 0%,#f7f7f7 100%); }
  .guide-hero-card,.guide-panel,.guide-doc-card { background:#fff; border:1px solid rgba(35,31,32,.08); border-radius:24px; box-shadow:0 18px 45px rgba(35,31,32,.08); }
  .guide-hero-card { display:grid; grid-template-columns: minmax(0,1.4fr) minmax(320px,1fr); gap:1.5rem; padding:2rem; }
  .guide-badge,.guide-section-heading span { display:inline-flex; width:fit-content; padding:.45rem .95rem; border-radius:999px; background:#beab74; color:#231f20; font-weight:600; }
  .guide-hero-main h2,.guide-section-heading h3,.guide-panel h3 { color:#231f20; }
  .guide-hero-main h2 { margin:1rem 0; font-size:clamp(2rem,4vw,3rem); line-height:1.15; }
  .guide-hero-main p,.guide-panel p,.guide-doc-card p,.guide-side-item { color:#5f5a5b; line-height:1.8; margin:0; }
  .guide-stats { margin-top:1.5rem; display:grid; grid-template-columns:repeat(2,minmax(0,1fr)); gap:1rem; }
  .guide-stat-card { padding:1rem 1.1rem; border-radius:18px; background:#beab74; }
  .guide-stat-card span { display:block; color:#fff; margin-bottom:.35rem; }
  .guide-stat-card strong { color:#231f20; font-size:1.2rem; }
  .guide-hero-side { padding:2rem; border-radius:24px; background:linear-gradient(0deg,#231f20 0%,#beab74 100%); color:#fff; }
  .guide-hero-side h3 { color:#fff; margin:1rem 0; }
  .guide-side-icon,.guide-doc-icon { width:62px; height:62px; display:inline-flex; align-items:center; justify-content:center; border-radius:50%; font-size:1.5rem; }
  .guide-side-icon { background:rgba(255,255,255,.12); }
  .guide-side-item { margin-top:1rem; padding:1rem; border-radius:18px; background:rgba(255,255,255,.08); color:#fff; }
  .guide-panel { padding:2rem; }
  .guide-panel-title { display:flex; align-items:center; gap:.85rem; margin-bottom:1rem; }
  .guide-list-item,.guide-process-item { display:flex; gap:.9rem; align-items:flex-start; padding:1rem; border-radius:18px; background:#f7f7f7; margin-top:1rem; }
  .guide-list-item svg { margin-top:.2rem; color:#beab74; flex-shrink:0; }
  .guide-step { width:38px; height:38px; border-radius:50%; background:#beab74; color:#000; display:inline-flex; align-items:center; justify-content:center; font-weight:700; flex-shrink:0; }
  .guide-section { margin-top:7rem; }
  .guide-section-heading { text-align:center; margin-bottom:1.75rem; }
  .guide-section-heading h3 { margin-top:.9rem; font-size:clamp(1.8rem,3vw,2.4rem); }
  .guide-doc-card { padding:1.6rem; }
  .guide-doc-icon { background:#231f20; color:#fff; margin-bottom:1rem; }
  @media (max-width:991px){ .guide-hero-card{grid-template-columns:1fr;padding:1.5rem}.guide-panel{padding:1.5rem} }
  @media (max-width:767px){ .guide-stats{grid-template-columns:1fr} .license-guide-page{padding:30px 0 60px} }
`;

export default A2LicensePage;

